import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { AuthContextType, AuthState, LoginCredentials, AuthResponse, Account, User } from '@/types/auth';
import axios from 'axios';
import { STORAGE_KEYS } from '@/config/constants';
import api, { isPublicTokenPath } from '@/utils/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { getUserProfile } from '@/pages/UserProfile/services';
import { toast } from 'react-toastify';
import CurrencyService from '@/services/currency.service';
import { setCurrencyContext } from '@/utils/currencyContext';
import {
    USER_DETAILS_QUERY_KEY,
    resetCacheForNewIdentity,
    resetCacheForSignOut,
} from '@/context/sessionCache';

const initialState: AuthState = {
    isAuthenticated: false,
    user: null,
    loading: false,
    error: null,
    impersonatedAccount: null
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    // Add debug logging
    // console.log('useAuth - Current context:', context);
    // console.log('useAuth - Current user:', context.auth.user);

    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [auth, setAuth] = useState<AuthState>(() => {
        // Check if user data exists in localStorage
        try {
            const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
            const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
            const storedImpersonatedAccount = localStorage.getItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT);

            if (storedUser && storedToken) {
                // Set axios default headers for all requests
                axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
                return {
                    isAuthenticated: true,
                    user: JSON.parse(storedUser || '{}'),
                    loading: false,
                    error: null,
                    impersonatedAccount: storedImpersonatedAccount ? JSON.parse(storedImpersonatedAccount || '{}') : null
                };
            }
        } catch (error) {
            // If there's an error parsing the stored user data, clear localStorage
            console.error('Error parsing stored user data:', error);
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            localStorage.removeItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT);
        }

        return initialState;
    });

    const queryClient = useQueryClient();
    const location = useLocation();

    // ask for logged in user details.
    // Suppressed on public token pages (proposal/work-order/pay links): a leftover
    // session from another account would otherwise fire GET /users/me, 401, and
    // trigger a refresh/redirect that knocks the recipient off the public page.
    const { data: userDetails } = useQuery({
        queryKey: [USER_DETAILS_QUERY_KEY],
        queryFn: getUserProfile,
        enabled: !!auth.user && !isPublicTokenPath(location.pathname)
    });

    // Task 2.9 — app-level bootstrap for the active-currency context (D21 wiring).
    // Same gating as the userDetails fetch above: any authenticated, non-public-token
    // session. Best-effort (fetchData never throws — see currency.service.ts /
    // utils/api.ts), and setCurrencyContext ignores an empty/missing list, so a
    // failed or slow fetch just leaves every currency-aware render on the
    // USD-only default (byte-identical to today) rather than breaking anything.
    const { data: activeCurrencies } = useQuery({
        queryKey: ['activeCurrencies'],
        queryFn: () => CurrencyService.getActiveCurrencies(),
        enabled: !!auth.user && !isPublicTokenPath(location.pathname),
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (activeCurrencies?.success && activeCurrencies.data) {
            setCurrencyContext(activeCurrencies.data.currencies);
        }
    }, [activeCurrencies]);

    useEffect(() => {
        if (userDetails) {
            // update user details, and save to localStorage
            setAuth(prev => ({ ...prev, user: userDetails as User }));
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userDetails));
        }
    }, [userDetails]);

    const authUser = useCallback((
        accessToken: string,
        refreshToken: string,
        userDetails: User,
        options?: { silent?: boolean }
    ): void => {
        setAuth(prev => ({ ...prev, loading: true, error: null }));

        if (!accessToken || !userDetails) {
            console.error('Auth details are missing');
            throw new Error('Auth details are missing');
        }
        if (!options?.silent) {
            toast.success('Login successful');
        }

        // Establish a CLEAN session for the new identity. authUser is used by the
        // public proposal/work-order sign + pay flows, where the recipient may
        // already have a stale session from a *different* account in localStorage.
        // We must not let the previous account's refresh token or impersonation
        // leak into the new session — otherwise a later 401 would refresh into the
        // wrong account (account mismatch on the order they are paying) or bounce
        // them to login. So: set the new access token, and either set the provided
        // refresh token or explicitly drop any prior one.
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);

        if (refreshToken) {
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
        } else {
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        }

        localStorage.removeItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userDetails));

        // Set axios default headers for all requests, and clear any impersonation
        // header carried over from a previous session.
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        delete axios.defaults.headers.common['X-Impersonated-Account'];

        setAuth({
            isAuthenticated: true,
            user: userDetails,
            loading: false,
            error: null,
            impersonatedAccount: null,
        });

        // The canonical profile (GET /users/me) may be cached from the PREVIOUS
        // session — REMOVE it, do not merely invalidate it.
        //
        // `invalidateQueries` marks the entry stale but keeps serving it while the
        // refetch is in flight. Because login is a client-side `navigate()` and not
        // a page load, that cached entry belongs to whoever was signed in before,
        // and the effect below would write THEIR profile into this session's
        // `auth.user` and localStorage for a render or two. That profile carries
        // `role`, `accountId`, and now the capability fields — `permissionFlags`,
        // `canManagePermissionFlags`, `canOverrideOrderPaidStatus` — so the new user
        // would briefly be shown buttons they do not hold, and `getRootRoute` in
        // pages/auth/LogIn could even land them using the previous user's role.
        //
        // `removeQueries` deletes the entry, so the query starts with `data`
        // undefined and the effect cannot fire until this user's own profile
        // arrives. The server re-checks every request anyway (verified: writes 403
        // for a non-permission-admin), so this was a display-only leak — but on the
        // one screen whose whole job is permissions, showing the wrong ones is not
        // acceptable.
        resetCacheForNewIdentity(queryClient);
    }, [queryClient]);

    const login = useCallback(async (credentials: LoginCredentials): Promise<AuthResponse> => {
        setAuth(prev => ({ ...prev, loading: true, error: null }));

        try {
            const response = await api.post('/auth/login', credentials);

            // Handle different response structures - check if data is nested
            let accessToken, refreshToken, user;

            if (response.data.data) {
                // Response with nested data structure { success, message, data: { accessToken, refreshToken, user } }
                accessToken = response.data.data.accessToken;
                refreshToken = response.data.data.refreshToken;
                user = response.data.data.user;
            } else {
                // Direct response { accessToken, refreshToken, user }
                accessToken = response.data.accessToken;
                refreshToken = response.data.refreshToken;
                user = response.data.user;
            }

            if (!accessToken || !user) {
                console.error('Invalid response format:', response.data);
                throw new Error('Invalid response from server');
            }


            // Store tokens and user in localStorage
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
            if (refreshToken) {
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
            }
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

            // Set axios default headers for all requests
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

            setAuth({
                isAuthenticated: true,
                user,
                loading: false,
                error: null,
            });

            return {
                success: true,
                message: 'Login successful',
                accessToken,
                refreshToken,
                user
            };
        } catch (error) {
            console.error('Login error:', error);
            let errorMessage = 'Login failed';

            if (axios.isAxiosError(error) && error.response) {
                console.error('Login error response:', error.response.data);
                errorMessage = error.response.data.message || errorMessage;
            }

            setAuth(prev => ({
                ...prev,
                loading: false,
                error: errorMessage,
            }));

            return {
                success: false,
                message: errorMessage
            };
        }
    }, []);

    const loginWithCode = useCallback(async (email: string, code: string): Promise<AuthResponse> => {
        setAuth(prev => ({ ...prev, loading: true, error: null }));

        try {
            const response = await api.post('/auth/login/verify-code', { email, code });

            // Handle different response structures - check if data is nested
            let accessToken, refreshToken, user;

            if (response.data.data) {
                // Response with nested data structure { success, message, data: { accessToken, refreshToken, user } }
                accessToken = response.data.data.accessToken;
                refreshToken = response.data.data.refreshToken;
                user = response.data.data.user;
            } else {
                // Direct response { accessToken, refreshToken, user }
                accessToken = response.data.accessToken;
                refreshToken = response.data.refreshToken;
                user = response.data.user;
            }

            if (!accessToken || !user) {
                console.error('Invalid response format:', response.data);
                throw new Error('Invalid response from server');
            }

            // Store tokens and user in localStorage
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
            if (refreshToken) {
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
            }
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

            // Set axios default headers for all requests
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

            setAuth({
                isAuthenticated: true,
                user,
                loading: false,
                error: null,
            });

            return {
                success: true,
                message: 'Login successful',
                accessToken,
                refreshToken,
                user
            };
        } catch (error) {
            console.error('Login with code error:', error);
            let errorMessage = 'Login with code failed';

            if (axios.isAxiosError(error) && error.response) {
                console.error('Login with code error response:', error.response.data);
                errorMessage = error.response.data.message || errorMessage;
            }

            setAuth(prev => ({
                ...prev,
                loading: false,
                error: errorMessage,
            }));

            return {
                success: false,
                message: errorMessage
            };
        }
    }, []);

    const requestEmailCode = useCallback(async (email: string): Promise<AuthResponse> => {
        try {
            await api.post('/auth/login/email-code', { email });
            return {
                success: true,
                message: 'Verification code sent to your email'
            };
        } catch (error) {
            let errorMessage = 'Failed to send verification code';

            if (axios.isAxiosError(error) && error.response) {
                errorMessage = error.response.data.message || errorMessage;
            }

            return {
                success: false,
                message: errorMessage
            };
        }
    }, []);



    const logout = useCallback((): void => {
        // AR read caches contain internal financial data and are session-only.
        queryClient.removeQueries({ queryKey: ['ar-aging'] });
        // Remove tokens and user from localStorage
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        localStorage.removeItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT);

        // Drop every cached query. All of it was fetched as the user who is
        // leaving, and login is a client-side `navigate()` rather than a page
        // load, so without this the next person to sign in on this tab is served
        // the previous user's cached data until each refetch lands — their
        // profile and capabilities, but also their leads, orders and accounts.
        // A page reload would discard all of it; a logout should too.
        resetCacheForSignOut(queryClient);

        // Remove axios default headers
        delete axios.defaults.headers.common['Authorization'];

        setAuth(initialState);
    }, [queryClient]);

    const clearError = useCallback((): void => {
        setAuth(prev => ({ ...prev, error: null }));
    }, []);

    // Impersonation functionality
    const impersonateAccount = useCallback((account: Account): void => {
        // Store the impersonated account
        localStorage.setItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT, JSON.stringify(account));

        // Update auth state with impersonated account
        setAuth(prev => ({
            ...prev,
            impersonatedAccount: account
        }));

        // Add impersonation header to all requests
        axios.defaults.headers.common['X-Impersonated-Account'] = account.id;
    }, []);

    const stopImpersonation = useCallback((): void => {
        // Remove impersonated account from localStorage
        localStorage.removeItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT);

        // Update auth state to remove impersonated account
        setAuth(prev => ({
            ...prev,
            impersonatedAccount: null
        }));

        // Remove impersonation header from requests
        delete axios.defaults.headers.common['X-Impersonated-Account'];
    }, []);

    return (
        <AuthContext.Provider value={{
            auth,
            user: auth.user,
            login,
            logout,
            clearError,
            loginWithCode,
            requestEmailCode,
            impersonateAccount,
            stopImpersonation,
            authUser,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
